import { useMount } from 'ahooks';
import { Col, Row, TableColumnsType, Tooltip } from 'antd';
import { useAppDispatch } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import { RevxDate } from 'components/RevxDate';
import { RevxMoney } from 'components/RevxMoney';
import { RevxTable } from 'components/RevxTable';
import { JobItem, RFMInvitation, RFMOffer } from 'domains/workspaceDomain';
import OfferResultChart from 'features/Workspace/components/OfferResultChart';
import OfferShippingSummary from 'features/Workspace/rfm/OfferShippingSummary';
import {
  offerShipDate,
  offerShippingMethod,
} from 'features/Workspace/rfm/offerShipping';
import { isEmpty, xor } from 'lodash-es';
import { Key, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './RFM.less';

const RFM = (jobData: JobItem) => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const [offers, setOffers] = useState<RFMOffer[]>([]);
  const [expandedRows, setExpandedRows] = useState<Key[]>([]);
  const [invitations, setInvitations] = useState<RFMInvitation[]>([]);

  useMount(async () => {
    const res = await dispatch.workspaceDetail.getRfmOffers(jobData.id);
    setOffers(res);
    if (!isEmpty(res)) {
      setInvitations(res[res.length - 1].invitations);
    }
  });

  const columns: TableColumnsType<RFMOffer> = [
    {
      title: t('workspace.rfm.offerTableColumns.round'),
      dataIndex: 'round',
      key: 'round',
      width: 120,
      render: (value: 1 | 2 | 3) => {
        switch (value) {
          case 1:
            return t('workspace.rfm.offerTableLabels.round1');
          case 2:
            return t('workspace.rfm.offerTableLabels.round2');
          case 3:
            return t('workspace.rfm.offerTableLabels.round3');
        }
      },
    },
    {
      title: t('workspace.rfm.tableColumns.turnTime'),
      dataIndex: 'turnTimeDays',
      key: 'turnTimeDays',
      width: 120,
      render: (value) => value + ' Days',
    },
    {
      title: t('workspace.rfm.offerTableColumns.buyerPrice'),
      dataIndex: 'buyerPrice',
      key: 'buyerPrice',
      width: 120,
      render: (value) => <RevxMoney value={value} />,
    },
    {
      title: t('workspace.rfm.offerTableColumns.shippingPrice'),
      dataIndex: 'shippingPrice',
      key: 'shippingPrice',
      width: 120,
      render: (value) => <RevxMoney value={value} />,
    },
    {
      title: t('workspace.rfm.offerTableColumns.supplierPrice'),
      dataIndex: 'supplierPrice',
      key: 'supplierPrice',
      width: 120,
      render: (value) => <RevxMoney value={value} />,
    },
    {
      title: (
        <Tooltip title={t('workspace.rfm.offerTableColumns.originalPoPriceTip')}>
          <span>{t('workspace.rfm.offerTableColumns.originalPoPrice')}</span>
        </Tooltip>
      ),
      key: 'originalPoPrice',
      width: 140,
      render: (value, record) =>
        record.originalPoPrice === null ? (
          '-'
        ) : (
          <RevxMoney value={record.originalPoPrice} />
        ),
    },
    {
      title: t('workspace.rfm.offerTableColumns.revshopMargin'),
      dataIndex: 'revshopMargin',
      key: 'revshopMargin',
      width: 120,
      render: (value) => <RevxMoney value={value} />,
    },
    {
      title: t('workspace.rfm.offerShipping.shipDate'),
      key: 'shipDate',
      width: 140,
      render: (value, record) => {
        const shipDate = offerShipDate(record);
        return shipDate ? <RevxDate dateStr={shipDate} /> : '-';
      },
    },
    {
      title: t('workspace.rfm.offerShipping.shippingMethod'),
      key: 'shippingMethod',
      width: 180,
      render: (value, record) => offerShippingMethod(record) || '-',
    },
    {
      title: t('workspace.rfm.offerTableColumns.expirationDate'),
      dataIndex: 'expireAt',
      key: 'expireAt',
      width: 160,
      render: (value) => <RevxDate dateStr={value} />,
    },
    {
      title: t('workspace.rfm.offerTableColumns.status'),
      key: 'status',
      width: 200,
      render: (value, record) =>
        record.cancelled ? (
          <Tooltip
            title={
              record.cancellationReason
                ? `${t('workspace.rfm.cancelRfm.reasonLabel')}: ${
                    record.cancellationReason
                  }`
                : undefined
            }
          >
            <span>
              {t('workspace.rfm.offerTableLabels.cancelled')}
              {record.cancelledAt ? ' · ' : ''}
              {record.cancelledAt && <RevxDate dateStr={record.cancelledAt} />}
            </span>
          </Tooltip>
        ) : (
          t('workspace.rfm.offerTableLabels.open')
        ),
    },
    {
      title: t('workspace.rfm.offerTableColumns.manufacturingTypes'),
      key: 'manufacturingTypes',
      width: 160,
      render: (value, record) => (
        <RevxButton
          type="link"
          size="small"
          onClick={() => setExpandedRows(xor(expandedRows, [record.round]))}
        >
          {t('workspace.rfm.actionButtons.viewTypes')}
        </RevxButton>
      ),
    },
  ];

  const pressTypes = (record: RFMOffer) => {
    return (
      <>
        {record.pressAdjustments?.map((pa) => (
          <Row key={pa.skuId}>
            <Col span={6} offset={2}>
              {pa.skuName}
            </Col>
            <Col span={10}>{pa.pressTypeCode}</Col>
          </Row>
        ))}
        <OfferShippingSummary offer={record} />
      </>
    );
  };

  return (
    <div className="rfm-card">
      <RevxTable<RFMOffer>
        onRow={(record) => ({
          onClick: () => setInvitations(record.invitations),
        })}
        columns={columns}
        dataSource={offers}
        rowKey={'round'}
        pagination={false}
        size="small"
        expandable={{
          expandedRowKeys: expandedRows,
          expandIconColumnIndex: -1,
          expandedRowRender: pressTypes,
        }}
      />
      <OfferResultChart invitations={invitations} />
    </div>
  );
};

export default RFM;
